(() => {
  'use strict';
  if (window.__leavysFilterAccessibility) return;
  window.__leavysFilterAccessibility = true;

  const html = document.documentElement;
  const openerSelector = '.gf-refine-toggle-mobile button, [data-gf-qf-drawer]';
  const sortSelector = '[data-leavys-filter-sort]';
  const filterActionSelector = 'a.gf-clear[data-fid]:not([href]), a.refine-by-multi-filter[data-fid]:not([href])';
  const focusSelector = 'button, a[href], input, select, textarea, [tabindex], [contenteditable="true"]';
  const background = new Map();
  let tree = null;
  let open = false;
  let previousModal = null;
  let opener = null;
  let lastFocused = null;
  let optimisticSort = null;
  let frame = 0;
  let suppressEscapeUp = false;
  let pendingFocus = null;

  const setAttribute = (element, name, value) => {
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  };
  const visible = element => Boolean(element && element.isConnected && element.getClientRects().length &&
    getComputedStyle(element).visibility !== 'hidden' && !element.closest('[hidden], [inert], [aria-hidden="true"]'));
  const focus = element => {
    if (visible(element)) element.focus({ preventScroll: true });
  };
  const tabbables = root => Array.from(root.querySelectorAll(focusSelector))
    .filter(element => element.tabIndex >= 0 && !element.matches(':disabled') && visible(element));
  const drawerMode = () => html.classList.contains('gf-offcanvas') ||
    window.matchMedia(`(max-width: ${Number(window.mobile_break_point) || 768}px)`).matches;
  const activeDrawer = () => tree && drawerMode() && html.classList.contains('offcanvas-open');
  const sortParts = sort => ({
    button: sort.querySelector('.sort-by-toggle'),
    list: sort.querySelector('.globo-dropdown-custom__options'),
    options: Array.from(sort.querySelectorAll('[role="option"][data-sort]'))
  });

  function releaseBackground() {
    background.forEach((wasInert, element) => { element.inert = wasInert; });
    background.clear();
  }

  function containBackground() {
    let branch = tree;
    while (branch && branch !== document.body) {
      const parent = branch.parentElement;
      if (!parent) break;
      Array.from(parent.children).forEach(element => {
        if (element === branch || element.id === 'gf-overlay' ||
            /^(SCRIPT|STYLE|LINK|META|TEMPLATE)$/.test(element.tagName)) return;
        if (!background.has(element)) background.set(element, element.inert);
        if (!element.inert) element.inert = true;
      });
      branch = parent;
    }
  }

  function focusStart() {
    const target = tree.querySelector('.gf-close-canvas');
    if (visible(target)) focus(target);
    else {
      const first = tabbables(tree)[0];
      if (first) focus(first);
      else {
        setAttribute(tree, 'tabindex', '-1');
        focus(tree);
      }
    }
  }

  function rememberFocus(element) {
    lastFocused = {
      element,
      id: element.id,
      block: element.closest('[data-filter-id]')?.getAttribute('data-filter-id'),
      value: element.getAttribute('data-value'),
      filterValue: element.getAttribute('data-fvalue'),
      filterId: element.getAttribute('data-fid'),
      sort: element.getAttribute('data-sort'),
      tag: element.tagName,
      text: element.textContent.trim()
    };
  }

  function recoverFocus() {
    if (!lastFocused || lastFocused.element.isConnected ||
        (document.activeElement !== document.body && document.activeElement !== html)) return;
    const candidates = Array.from(tree.querySelectorAll(focusSelector));
    const match = candidates.find(element => lastFocused.id && element.id === lastFocused.id) ||
      candidates.find(element => element.tagName === lastFocused.tag &&
        element.closest('[data-filter-id]')?.getAttribute('data-filter-id') === lastFocused.block &&
        element.getAttribute('data-value') === lastFocused.value &&
        element.getAttribute('data-fvalue') === lastFocused.filterValue &&
        element.getAttribute('data-fid') === lastFocused.filterId &&
        element.getAttribute('data-sort') === lastFocused.sort &&
        (lastFocused.filterValue !== null || lastFocused.filterId !== null ||
          lastFocused.sort !== null || element.textContent.trim() === lastFocused.text));
    if (visible(match)) focus(match);
    else focusStart();
  }

  function queueFocusRestore(source, findTarget, awaitingRender = true) {
    pendingFocus = { source, findTarget, target: null, awaitingRender };
    schedule();
  }

  function restoreActionFocus() {
    if (!pendingFocus) return false;
    const pending = pendingFocus;
    const active = document.activeElement;
    if (active !== document.body && active !== html && active !== pending.source && active !== pending.target) {
      pendingFocus = null;
      return false;
    }
    const target = pending.findTarget();
    if (visible(target)) {
      pending.target = target;
      focus(target);
    }
    if (!pending.awaitingRender) pendingFocus = null;
    return true;
  }

  function queueToolbarSortFocus(option) {
    const container = option.closest('.sort-by');
    const button = container?.querySelector('.sort-by-toggle');
    if (!button || tree?.contains(container)) return;
    queueFocusRestore(option, () => {
      if (button.isConnected) return button;
      return Array.from(document.querySelectorAll('.sort-by-toggle'))
        .find(element => !tree?.contains(element) && visible(element));
    }, option.getAttribute('data-sort') !== currentSort());
  }

  function queueFilterActionFocus(action) {
    const filterId = action.getAttribute('data-fid');
    const filterValue = action.getAttribute('data-fvalue');
    queueFocusRestore(action, () => {
      const block = Array.from(tree?.querySelectorAll('[data-filter-id]') || [])
        .find(element => element.getAttribute('data-filter-id') === filterId);
      const option = filterValue !== null && Array.from(block?.querySelectorAll('[data-fvalue]') || [])
        .find(element => element.getAttribute('data-fvalue') === filterValue && visible(element));
      if (option) return option;
      const heading = block?.querySelector('.gf-block-title h3, .gf-block-title .h3');
      if (visible(heading)) return heading;
      return Array.from(document.querySelectorAll(openerSelector)).find(visible);
    });
  }

  function repairFilterActions() {
    document.querySelectorAll(filterActionSelector).forEach(action => {
      setAttribute(action, 'role', 'button');
      if (!action.hasAttribute('tabindex')) setAttribute(action, 'tabindex', '0');
      setAttribute(action, 'data-leavys-filter-action', '');
    });
  }

  function closeSort(sort, returnFocus = false) {
    const { button, list } = sortParts(sort);
    if (!button || !list) return;
    setAttribute(button, 'aria-expanded', 'false');
    list.hidden = true;
    sort.classList.remove('active');
    if (returnFocus) focus(button);
  }

  function selectedIndex(sort) {
    const { button, options } = sortParts(sort);
    const current = currentSort();
    const currentIndex = options.findIndex(option => option.getAttribute('data-sort') === current);
    if (currentIndex >= 0) return currentIndex;
    const selected = options.findIndex(option => option.getAttribute('aria-selected') === 'true');
    if (selected >= 0) return selected;
    const index = options.findIndex(option => option.textContent.trim() === button?.querySelector('span')?.textContent.trim());
    return Math.max(0, index);
  }

  function currentSort() {
    const appSort = window.globofilter?.state?.queryParams?.sort_by;
    if (typeof appSort === 'string' && appSort) return appSort;
    return optimisticSort || new URL(window.location.href).searchParams.get('sort_by');
  }

  function focusOption(sort, index) {
    const { options } = sortParts(sort);
    options.forEach((option, position) => setAttribute(option, 'tabindex', position === index ? '0' : '-1'));
    focus(options[index]);
  }

  function openSort(sort, last = false) {
    const { button, list, options } = sortParts(sort);
    if (!button || !list || !options.length) return;
    tree.querySelectorAll(sortSelector).forEach(other => { if (other !== sort) closeSort(other); });
    list.hidden = false;
    setAttribute(button, 'aria-expanded', 'true');
    sort.classList.add('active');
    focusOption(sort, last ? options.length - 1 : selectedIndex(sort));
  }

  function chooseSort(sort, option) {
    if (typeof window.changeSortBy !== 'function') return;
    const value = option.getAttribute('data-sort');
    const label = option.textContent.trim();
    window.changeSortBy(value);
    optimisticSort = value;
    const { button, options } = sortParts(sort);
    const text = button?.querySelector('span');
    if (text) text.textContent = label;
    options.forEach(item => setAttribute(item, 'aria-selected', item === option ? 'true' : 'false'));
    closeSort(sort, true);
  }

  function repairSorts() {
    if (typeof window.changeSortBy !== 'function') return;
    tree.querySelectorAll('.sort-by').forEach((sort, index) => {
      const { button, list, options } = sortParts(sort);
      if (!button || !list) return;
      const buttonId = `leavys-filter-sort-${index + 1}`;
      const listId = `leavys-filter-sort-options-${index + 1}`;
      setAttribute(sort, 'data-leavys-filter-sort', '');
      setAttribute(button, 'id', buttonId);
      setAttribute(button, 'aria-labelledby', buttonId);
      setAttribute(button, 'aria-controls', listId);
      setAttribute(button, 'aria-haspopup', 'listbox');
      setAttribute(list, 'id', listId);
      const expanded = button.getAttribute('aria-expanded') === 'true';
      if (list.hidden === expanded) list.hidden = !expanded;
      const current = currentSort();
      const currentIndex = options.findIndex(option => option.getAttribute('data-sort') === current);
      if (currentIndex >= 0 || !options.some(option => option.hasAttribute('aria-selected'))) {
        const selected = selectedIndex(sort);
        options.forEach((option, position) => setAttribute(option, 'aria-selected', position === selected ? 'true' : 'false'));
        const label = button.querySelector('span');
        const text = options[selected]?.textContent.trim();
        if (label && text && label.textContent !== text) label.textContent = text;
      }
    });
  }

  function sync() {
    frame = 0;
    const next = document.getElementById('gf-tree');
    if (!next) {
      releaseBackground();
      if (open) focus(opener);
      tree = null;
      open = false;
      previousModal = null;
      return;
    }
    tree = next;
    repairSorts();
    repairFilterActions();
    const modal = drawerMode();
    const nextOpen = modal && html.classList.contains('offcanvas-open');
    if (!modal && html.classList.contains('offcanvas-open')) {
      if (typeof window.globofilter?.toggleCanvas === 'function') window.globofilter.toggleCanvas();
      else tree.querySelector('.gf-close-canvas')?.click();
    }

    if (open && !nextOpen) {
      releaseBackground();
      tree.querySelectorAll(sortSelector).forEach(sort => closeSort(sort));
      if (modal) focus(visible(opener) ? opener : Array.from(document.querySelectorAll(openerSelector)).find(visible));
      lastFocused = null;
    }
    if (modal && !nextOpen && (tree.contains(document.activeElement) ||
        (previousModal === false && document.activeElement === document.body &&
          lastFocused && tree.contains(lastFocused.element)))) {
      focus(visible(opener) ? opener : Array.from(document.querySelectorAll(openerSelector)).find(visible));
      lastFocused = null;
    }
    tree.inert = modal && !nextOpen;
    setAttribute(tree, 'aria-hidden', modal && !nextOpen ? 'true' : 'false');
    if (modal) {
      setAttribute(tree, 'role', 'dialog');
      setAttribute(tree, 'aria-modal', 'true');
      const heading = tree.querySelector('.gf-filter-heading');
      if (heading) {
        if (!heading.id) heading.id = 'gf-filter-title';
        setAttribute(tree, 'aria-labelledby', heading.id);
      }
    } else {
      tree.removeAttribute('role');
      tree.removeAttribute('aria-modal');
      releaseBackground();
    }

    document.querySelectorAll(openerSelector).forEach(button => {
      setAttribute(button, 'aria-controls', 'gf-tree');
      setAttribute(button, 'aria-expanded', nextOpen ? 'true' : 'false');
    });

    if (nextOpen) {
      releaseBackground();
      if (!open) {
        if (!opener || !opener.isConnected) opener = document.activeElement;
        focusStart();
      }
      containBackground();
      if (!restoreActionFocus()) recoverFocus();
    } else restoreActionFocus();
    open = nextOpen;
    previousModal = modal;
  }

  function schedule() {
    if (!frame) frame = requestAnimationFrame(sync);
  }

  function closeDrawer() {
    if (!activeDrawer()) return;
    const close = tree.querySelector('.gf-close-canvas');
    if (close) close.click();
    else if (typeof window.globofilter?.toggleCanvas === 'function') window.globofilter.toggleCanvas();
    schedule();
  }

  function onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    pendingFocus = null;
    const filterAction = target.closest(filterActionSelector);
    if (filterAction) queueFilterActionFocus(filterAction);
    const toolbarOption = target.closest('.sort-by [role="option"][data-sort]');
    if (toolbarOption && !tree?.contains(toolbarOption)) queueToolbarSortFocus(toolbarOption);
    const trigger = target.closest(openerSelector);
    if (trigger && !activeDrawer()) opener = trigger;
    const sort = target.closest(sortSelector);
    if (sort && tree?.contains(sort)) {
      const button = target.closest('.sort-by-toggle');
      const option = target.closest('[role="option"][data-sort]');
      if (button || option) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (option) chooseSort(sort, option);
        else if (button.getAttribute('aria-expanded') === 'true') closeSort(sort, true);
        else openSort(sort);
      }
    } else tree?.querySelectorAll(sortSelector).forEach(item => closeSort(item));
    schedule();
  }

  function onKeyDown(event) {
    pendingFocus = null;
    const target = event.target instanceof Element ? event.target : null;
    const filterAction = target?.closest('[data-leavys-filter-action]');
    if (filterAction && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!event.repeat) filterAction.click();
      return;
    }
    const toolbarOption = target?.closest('.sort-by [role="option"][data-sort]');
    if (toolbarOption && !tree?.contains(toolbarOption) && (event.key === 'Enter' || event.key === ' ')) {
      queueToolbarSortFocus(toolbarOption);
    }
    const sort = target?.closest(sortSelector);
    if (sort && tree?.contains(sort)) {
      const { button, options } = sortParts(sort);
      const option = target.closest('[role="option"][data-sort]');
      const index = options.indexOf(option);
      const expanded = button.getAttribute('aria-expanded') === 'true';
      if (event.key === 'Tab') {
        closeSort(sort, Boolean(option));
        if (option && event.shiftKey) {
          event.preventDefault();
          return;
        }
      }
      else if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' ', 'Escape'].includes(event.key)) {
        if (event.key !== 'Escape' || expanded) {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (event.key === 'Escape') {
            suppressEscapeUp = true;
            closeSort(sort, true);
          } else if (option && (event.key === 'Enter' || event.key === ' ')) chooseSort(sort, option);
          else if (!expanded) openSort(sort, event.key === 'ArrowUp');
          else if (options.length) {
            const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 :
              event.key === 'ArrowUp' ? (index - 1 + options.length) % options.length : (index + 1) % options.length;
            focusOption(sort, next);
          }
          return;
        }
      }
    }
    if (!activeDrawer()) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      suppressEscapeUp = true;
      closeDrawer();
    } else if (event.key === 'Tab') {
      const items = tabbables(tree);
      const first = items[0];
      const last = items[items.length - 1];
      if (!first) {
        event.preventDefault();
        focusStart();
      } else if (event.shiftKey && (document.activeElement === first || !tree.contains(document.activeElement))) {
        event.preventDefault();
        focus(last);
      } else if (!event.shiftKey && (document.activeElement === last || !tree.contains(document.activeElement))) {
        event.preventDefault();
        focus(first);
      }
    }
  }

  function init() {
    document.addEventListener('click', onClick, true);
    document.addEventListener('pointerdown', () => { pendingFocus = null; }, true);
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('keyup', event => {
      if (event.key === 'Escape' && suppressEscapeUp) {
        suppressEscapeUp = false;
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);
    document.addEventListener('focusin', event => {
      if (pendingFocus && event.target !== pendingFocus.source && event.target !== pendingFocus.target &&
          event.target !== document.body && event.target !== html) pendingFocus = null;
      if (tree?.contains(event.target)) rememberFocus(event.target);
      else if (activeDrawer()) focusStart();
      else if (event.target !== document.body && event.target !== html) lastFocused = null;
    }, true);
    new MutationObserver(schedule).observe(html, { attributes: true, attributeFilter: ['class'] });
    new MutationObserver(records => {
      if (records.some(record => record.target === document.body ||
          record.target.closest?.('#gf-tree, .gf-actions, #gf-controls-container, .globo-selected-items-wrapper') ||
          Array.from(record.addedNodes).some(node => node.nodeType === 1 &&
            (node.id === 'gf-tree' || node.querySelector?.('#gf-tree'))))) schedule();
    }).observe(document.body, { childList: true, subtree: true });
    window.addEventListener('globoFilterRenderCompleted', () => {
      optimisticSort = null;
      schedule();
    });
    window.addEventListener('globoFilterTreeRenderCompleted', () => {
      if (pendingFocus) pendingFocus.awaitingRender = false;
      schedule();
    });
    window.addEventListener('popstate', () => { optimisticSort = null; schedule(); });
    window.addEventListener('resize', schedule);
    sync();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
